'use client';

import { useState, useMemo } from 'react';
import { Search, Plus, Eye, FileText, Copy, Trash2 } from 'lucide-react';
import { DataTable } from '../../../components/shared/DataTable';
import { PageHeader } from '../../../components/shared/PageHeader';
import { Modal, FormField, FormInput, FormSelect, FormTextarea, Button } from '../../../components/shared/Modal';
import { quotations, customers } from '../data/mock-data';
import { QUOTATION_STATUS_LABELS, QUOTATION_STATUS_COLORS } from '../types';
import type { Quotation, QuotationStatus } from '../types';
import { formatCurrency } from '../../../types';

// 견적서 작성 모달
function QuotationAddModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [items, setItems] = useState([{ itemName: '', quantity: 1, unitPrice: 0 }]);

  const addItem = () => {
    setItems([...items, { itemName: '', quantity: 1, unitPrice: 0 }]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="견적서 작성"
      description="새로운 견적서를 작성합니다."
      size="xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button variant="outline">초안 저장</Button>
          <Button onClick={onClose}>저장</Button>
        </>
      }
    >
      <div className="space-y-6">
        {/* 기본 정보 */}
        <div className="grid grid-cols-2 gap-4">
          <FormField label="견적번호" required>
            <FormInput placeholder="자동 생성" disabled />
          </FormField>
          <FormField label="고객" required>
            <FormSelect>
              <option value="">고객을 선택하세요</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>{customer.name}</option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="유효기한" required>
            <FormInput type="date" />
          </FormField>
          <FormField label="담당자">
            <FormInput placeholder="담당자명" />
          </FormField>
        </div>

        {/* 품목 목록 */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium">품목 목록</h3>
            <Button variant="outline" size="sm" onClick={addItem}>
              <Plus className="w-4 h-4 mr-1" />
              품목 추가
            </Button>
          </div>
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium">품목명</th>
                  <th className="text-right p-3 font-medium w-24">수량</th>
                  <th className="text-right p-3 font-medium w-32">단가</th>
                  <th className="text-right p-3 font-medium w-32">금액</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr key={index} className="border-t border-border">
                    <td className="p-2">
                      <FormInput placeholder="품목명" />
                    </td>
                    <td className="p-2">
                      <FormInput type="number" min={1} defaultValue={1} className="text-right" />
                    </td>
                    <td className="p-2">
                      <FormInput type="number" min={0} defaultValue={0} className="text-right" />
                    </td>
                    <td className="p-2 text-right font-medium">
                      ₩0
                    </td>
                    <td className="p-2">
                      <button
                        onClick={() => removeItem(index)}
                        className="p-1 rounded hover:bg-accent"
                        disabled={items.length === 1}
                      >
                        <Trash2 className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 금액 정보 */}
        <div className="flex justify-end">
          <div className="w-64 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">소계</span>
              <span>₩0</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">부가세 (10%)</span>
              <span>₩0</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">할인</span>
              <FormInput type="number" min={0} defaultValue={0} className="w-24 text-right h-8" />
            </div>
            <div className="flex justify-between pt-2 border-t border-border font-bold text-base">
              <span>합계</span>
              <span>₩0</span>
            </div>
          </div>
        </div>

        {/* 비고 */}
        <FormField label="비고">
          <FormTextarea rows={3} placeholder="견적 관련 메모를 입력하세요" />
        </FormField>
      </div>
    </Modal>
  );
}

export function QuotationListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<QuotationStatus | 'all'>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredData = useMemo(() => {
    return quotations.filter((quotation) => {
      // 검색 필터
      const matchesSearch =
        quotation.quotationNo.toLowerCase().includes(searchQuery.toLowerCase()) ||
        quotation.customerName.toLowerCase().includes(searchQuery.toLowerCase());

      // 상태 필터
      const matchesStatus =
        selectedStatus === 'all' || quotation.status === selectedStatus;

      return matchesSearch && matchesStatus;
    });
  }, [searchQuery, selectedStatus]);

  const columns = [
    {
      key: 'quotationNo',
      label: '견적번호',
      width: '14%',
      sortable: true,
      render: (value: unknown) => (
        <span className="font-mono text-sm">{value as string}</span>
      ),
    },
    {
      key: 'customerName',
      label: '고객',
      width: '18%',
      sortable: true,
      render: (value: unknown) => (
        <span className="truncate block">{value as string}</span>
      ),
    },
    {
      key: 'items',
      label: '품목',
      width: '8%',
      render: (value: unknown) => (
        <span className="whitespace-nowrap">{(value as Quotation['items']).length}개</span>
      ),
    },
    {
      key: 'total',
      label: '금액',
      width: '14%',
      align: 'right' as const,
      sortable: true,
      render: (value: unknown) => (
        <span className="font-medium whitespace-nowrap">{formatCurrency(value as number)}</span>
      ),
    },
    {
      key: 'validUntil',
      label: '유효기한',
      width: '12%',
      sortable: true,
      render: (value: unknown) => {
        const date = new Date(value as string);
        const isExpired = date < new Date();
        return (
          <span className={`whitespace-nowrap ${isExpired ? 'text-red-500' : ''}`}>
            {date.toLocaleDateString('ko-KR')}
          </span>
        );
      },
    },
    {
      key: 'status',
      label: '상태',
      width: '10%',
      render: (value: unknown) => {
        const status = value as QuotationStatus;
        return (
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${QUOTATION_STATUS_COLORS[status]}`}>
            {QUOTATION_STATUS_LABELS[status]}
          </span>
        );
      },
    },
    {
      key: 'createdAt',
      label: '작성일',
      width: '12%',
      sortable: true,
      render: (value: unknown) => (
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {new Date(value as string).toLocaleDateString('ko-KR')}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: '12%',
      render: (_: unknown, row: Quotation) => (
        <div className="flex items-center gap-1">
          <button className="p-1.5 rounded hover:bg-accent" title="상세보기">
            <Eye className="w-4 h-4 text-muted-foreground" />
          </button>
          <button className="p-1.5 rounded hover:bg-accent" title="복사">
            <Copy className="w-4 h-4 text-muted-foreground" />
          </button>
          <button className="p-1.5 rounded hover:bg-accent" title="인쇄">
            <FileText className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      ),
    },
  ];

  // 상태별 통계
  const statusStats = useMemo(() => {
    const stats: Record<QuotationStatus, number> = {
      draft: 0,
      submitted: 0,
      open: 0,
      ordered: 0,
      lost: 0,
      cancelled: 0,
    };
    quotations.forEach((q) => {
      stats[q.status]++;
    });
    return stats;
  }, []);

  return (
    <div className="space-y-6">
      <PageHeader
        title="견적서 관리"
        description="고객 견적서를 관리합니다."
        actions={
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            견적서 작성
          </button>
        }
      />

      {/* 상태별 통계 */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
        {(Object.keys(QUOTATION_STATUS_LABELS) as QuotationStatus[]).map((status) => (
          <button
            key={status}
            onClick={() => setSelectedStatus(selectedStatus === status ? 'all' : status)}
            className={`p-3 rounded-lg border transition-all ${
              selectedStatus === status
                ? 'border-primary bg-primary/5'
                : 'border-border bg-card hover:border-primary/50'
            }`}
          >
            <div className="text-xs text-muted-foreground">{QUOTATION_STATUS_LABELS[status]}</div>
            <div className="text-xl font-bold mt-1">{statusStats[status]}</div>
          </button>
        ))}
      </div>

      {/* 필터 영역 */}
      <div className="flex flex-wrap gap-4">
        {/* 검색 */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="견적번호, 고객명 검색..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* 상태 필터 */}
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value as QuotationStatus | 'all')}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 상태</option>
          {(Object.keys(QUOTATION_STATUS_LABELS) as QuotationStatus[]).map((status) => (
            <option key={status} value={status}>
              {QUOTATION_STATUS_LABELS[status]}
            </option>
          ))}
        </select>
      </div>

      {/* 데이터 테이블 */}
      <DataTable
        data={filteredData}
        columns={columns}
        pageSize={10}
        emptyMessage="검색 결과가 없습니다."
      />

      {/* 견적서 작성 모달 */}
      <QuotationAddModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
